import { clientEnv, runtimeEnvironment } from '@/lib/env';
import {
  useUser as useClerkUser,
  useAuth as useClerkAuth,
  useOrganization as useClerkOrganization,
  useOrganizationList as useClerkOrganizationList,
  useClerk as useClerkClerk,
} from '@clerk/nextjs';

// Check if dev auth bypass is enabled
const isDevBypass =
  runtimeEnvironment === 'development' &&
  clientEnv.NEXT_PUBLIC_DEV_AUTH_BYPASS === true;

// Mock user data for dev bypass mode
const mockUser = {
  id: 'dev_user_local',
  primaryEmailAddress: { emailAddress: 'dev@example.com' },
  firstName: 'Dev',
  lastName: 'User',
  fullName: 'Dev User',
  imageUrl: '',
  publicMetadata: {},
  unsafeMetadata: {},
  update: async () => {},
} as unknown as NonNullable<ReturnType<typeof useClerkUser>['user']>;

// Mock auth for dev bypass (shows as signed in)
const mockAuth = {
  isLoaded: true,
  isSignedIn: true,
  userId: 'dev_user_local',
  orgId: 'dev_org_local',
  sessionId: 'dev_session',
  getToken: async () => 'dev_token',
  signOut: async () => {},
} as unknown as ReturnType<typeof useClerkAuth>;

// Mock organization for dev bypass
const mockOrganization = {
  id: 'dev_org_local',
  name: 'Dev Organization',
  slug: 'dev-org',
  imageUrl: '',
  membersCount: 1,
  publicMetadata: {},
} as unknown as NonNullable<
  ReturnType<typeof useClerkOrganization>['organization']
>;

// Mock organization return value for dev bypass
const mockOrganizationReturn = {
  isLoaded: true,
  organization: mockOrganization,
  membership: { role: 'org:admin' },
} as unknown as ReturnType<typeof useClerkOrganization>;

// Mock organization list return value for dev bypass
const mockOrganizationListReturn = {
  isLoaded: true,
  organizationList: [
    { organization: mockOrganization, membership: { role: 'org:admin' } },
  ],
  userMemberships: { data: [{ organization: mockOrganization }] },
  setActive: async () => {},
  createOrganization: async () => mockOrganization,
} as unknown as ReturnType<typeof useClerkOrganizationList>;

// Mock user return value for dev bypass
const mockUserReturn = {
  user: mockUser,
  isLoaded: true,
  isSignedIn: true,
} as unknown as ReturnType<typeof useClerkUser>;

// Mock clerk instance for dev bypass
const mockClerk = {
  signOut: async () => {},
  openUserProfile: () => {},
  openSignIn: () => {},
  openSignUp: () => {},
  openOrganizationProfile: () => {},
  openCreateOrganization: () => {},
  user: mockUser,
  session: { id: 'dev_session_123' },
} as unknown as ReturnType<typeof useClerkClerk>;

/**
 * Safe useUser hook that works with dev bypass mode.
 * When DEV_AUTH_BYPASS is enabled, returns mock user data without calling Clerk.
 * This allows developing without Clerk authentication.
 */
export function useUser() {
  // In dev bypass mode, return mock data without calling Clerk hooks
  if (isDevBypass) {
    return mockUserReturn;
  }

  // Normal mode: use real Clerk hook
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useClerkUser();
}

/**
 * Safe useAuth hook that works with dev bypass mode.
 * When DEV_AUTH_BYPASS is enabled, returns mock auth data without calling Clerk.
 */
export function useAuth() {
  // In dev bypass mode, return mock auth without calling Clerk hooks
  if (isDevBypass) {
    return mockAuth;
  }

  // Normal mode: use real Clerk hook
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useClerkAuth();
}

/**
 * Safe useOrganization hook that works with dev bypass mode.
 * When DEV_AUTH_BYPASS is enabled, returns mock organization data without calling Clerk.
 */
export function useOrganization(
  params?: Parameters<typeof useClerkOrganization>[0]
) {
  // In dev bypass mode, return mock organization without calling Clerk hooks
  if (isDevBypass) {
    return mockOrganizationReturn;
  }

  // Normal mode: use real Clerk hook
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useClerkOrganization(params);
}

/**
 * Safe useOrganizationList hook that works with dev bypass mode.
 * When DEV_AUTH_BYPASS is enabled, returns mock organization list without calling Clerk.
 */
export function useOrganizationList(
  params?: Parameters<typeof useClerkOrganizationList>[0]
) {
  // In dev bypass mode, return mock organization list without calling Clerk hooks
  if (isDevBypass) {
    return mockOrganizationListReturn;
  }

  // Normal mode: use real Clerk hook
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useClerkOrganizationList(params);
}

/**
 * Safe useClerk hook that works with dev bypass mode.
 * When DEV_AUTH_BYPASS is enabled, returns mock clerk instance without calling Clerk.
 */
export function useClerk() {
  // In dev bypass mode, return mock clerk without calling Clerk hooks
  if (isDevBypass) {
    return mockClerk;
  }

  // Normal mode: use real Clerk hook
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useClerkClerk();
}
